import { useState, useEffect } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { 
  Sparkles, 
  Trash2, 
  RefreshCw, 
  X,
  Lightbulb,
  Users,
  Briefcase,
  CreditCard,
  Receipt
} from "lucide-react";

export default function OnboardingBanner({ onDismiss }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await apiClient.get("/onboarding/status");
      setStatus(response.data);
    } catch (error) {
      console.error("Error fetching onboarding status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSampleData = async () => {
    setActionLoading(true);
    try {
      const response = await apiClient.delete("/onboarding/sample-data");
      toast.success("Données d'exemple supprimées !");
      setStatus({ ...status, has_sample_data: false });
      if (onDismiss) onDismiss();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRegenerateSampleData = async () => {
    setActionLoading(true);
    try {
      const response = await apiClient.post("/onboarding/regenerate");
      toast.success("Données d'exemple régénérées !");
      await fetchStatus();
    } catch (error) {
      toast.error("Erreur lors de la régénération");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem("onboarding_dismissed", "true");
  };

  // Ne pas afficher si chargement, pas de données d'exemple, ou banner fermée
  if (loading || !status?.has_sample_data || dismissed) {
    return null;
  }

  const { sample_counts } = status;

  return (
    <Card className="bg-gradient-to-r from-blue-950/40 via-indigo-950/30 to-violet-950/40 border-blue-800/50 mb-6 relative overflow-hidden" data-testid="onboarding-banner">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-64 h-64 opacity-5 pointer-events-none">
        <Sparkles className="w-full h-full" />
      </div>
      
      <CardContent className="p-4 md:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Left side - Info */}
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-600/20 flex items-center justify-center flex-shrink-0">
              <Lightbulb className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-semibold text-white">Mode Découverte</h3>
                <Badge className="bg-blue-600/30 text-blue-300 text-xs">DÉMO</Badge>
              </div>
              <p className="text-sm text-zinc-400 mb-3">
                Des données d'exemple sont disponibles pour vous aider à découvrir l'application.
                Supprimez-les quand vous êtes prêt à utiliser vos vraies données.
              </p>
              
              {/* Sample data counts */}
              <div className="flex flex-wrap gap-3">
                {sample_counts?.clients > 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-zinc-400 bg-zinc-900/50 px-2 py-1 rounded">
                    <Users className="w-3 h-3" />
                    <span>{sample_counts.clients} clients</span>
                  </div>
                )}
                {sample_counts?.missions > 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-zinc-400 bg-zinc-900/50 px-2 py-1 rounded">
                    <Briefcase className="w-3 h-3" />
                    <span>{sample_counts.missions} missions</span>
                  </div>
                )}
                {sample_counts?.paiements > 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-zinc-400 bg-zinc-900/50 px-2 py-1 rounded">
                    <CreditCard className="w-3 h-3" />
                    <span>{sample_counts.paiements} paiements</span>
                  </div>
                )}
                {sample_counts?.depenses > 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-zinc-400 bg-zinc-900/50 px-2 py-1 rounded">
                    <Receipt className="w-3 h-3" />
                    <span>{sample_counts.depenses} dépenses</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right side - Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              onClick={handleRegenerateSampleData}
              disabled={actionLoading}
            >
              <RefreshCw className={`w-4 h-4 mr-1.5 ${actionLoading ? 'animate-spin' : ''}`} />
              Régénérer
            </Button>
            
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="default"
                  size="sm"
                  className="bg-red-600/80 hover:bg-red-600 text-white"
                  disabled={actionLoading}
                >
                  <Trash2 className="w-4 h-4 mr-1.5" />
                  Supprimer les exemples
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-white">Supprimer les données d'exemple ?</AlertDialogTitle>
                  <AlertDialogDescription className="text-zinc-400">
                    Cette action supprimera définitivement toutes les données d'exemple 
                    ({sample_counts?.clients || 0} clients, {sample_counts?.missions || 0} missions, etc.).
                    Vous pourrez ensuite créer vos propres données.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-zinc-800 text-zinc-300 border-zinc-700 hover:bg-zinc-700">
                    Annuler
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteSampleData}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    Supprimer
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {/* Close button */}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800"
              onClick={handleDismiss}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
